/**
 * Event detail pricing & map — aligned with Vibes mobile `detailScreen` / `getPriceForDay`.
 */

const DAY_ORDER = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
  { key: "sunday", label: "Sun" },
];

/** Try common API key shapes (monday, Monday, Mon, MON). */
function readDayValue(obj, dayKey, dayLabel) {
  if (!obj || typeof obj !== "object") return null;
  const cap = dayKey.charAt(0).toUpperCase() + dayKey.slice(1);
  const candidates = [
    dayKey,
    cap,
    dayKey.toUpperCase(),
    dayLabel,
    dayLabel.toUpperCase(),
    dayLabel.toLowerCase(),
  ];
  for (const k of candidates) {
    if (k in obj && obj[k] != null && obj[k] !== "") {
      const n = Number(obj[k]);
      if (!Number.isNaN(n)) return n;
    }
  }
  return null;
}

export function getPriceForDay(val, dayKey, dayLabel = "") {
  if (val == null) return null;
  if (typeof val === "number" && !Number.isNaN(val)) return val;
  if (typeof val === "string" && val.trim() !== "") {
    const n = Number(val);
    return !Number.isNaN(n) ? n : null;
  }
  if (typeof val === "object") {
    const direct = readDayValue(val, dayKey, dayLabel);
    if (direct != null) return direct;
    const legacy = val[dayKey];
    if (legacy != null) {
      const n = Number(legacy);
      return !Number.isNaN(n) ? n : null;
    }
  }
  return null;
}

/** Today’s key: sunday … saturday (same as mobile). */
export function getCurrentDayKey() {
  const days = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  return days[new Date().getDay()];
}

export function getPriceForCurrentDay(val) {
  const key = getCurrentDayKey();
  const label = DAY_ORDER.find((d) => d.key === key)?.label ?? "";
  return getPriceForDay(val, key, label);
}

/** Calendar YYYY-MM-DD from API value (ISO date is taken from the date part, not UTC instant). */
export function parseEventCalendarYmd(dateRaw) {
  if (dateRaw == null || dateRaw === "") return "";
  const trimmed = String(dateRaw).trim();
  const isoPrefix = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed);
  if (isoPrefix) return isoPrefix[1];
  const dt = new Date(trimmed);
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Display label for event start/end — same calendar day for all users (no timezone shift). */
export function formatEventDateLabel(dateRaw, { fallback = "Date TBD" } = {}) {
  const ymd = parseEventCalendarYmd(dateRaw);
  if (!ymd) return fallback;
  const [y, m, d] = ymd.split("-").map(Number);
  const local = new Date(y, m - 1, d);
  if (Number.isNaN(local.getTime())) return fallback;
  return local.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function dayKeyFromDateRaw(dateRaw) {
  if (!dateRaw) return "";
  const days = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  const ymd = parseEventCalendarYmd(dateRaw);
  if (ymd) {
    const [y, m, d] = ymd.split("-").map(Number);
    const local = new Date(y, m - 1, d);
    if (!Number.isNaN(local.getTime())) return days[local.getDay()];
  }
  const dt = new Date(dateRaw);
  if (Number.isNaN(dt.getTime())) return "";
  return days[dt.getDay()];
}

/** Price for a given calendar date (event day), with flat / first-day fallbacks. */
export function getPriceForEventDate(val, eventDateRaw) {
  const dayKey = dayKeyFromDateRaw(eventDateRaw) || getCurrentDayKey();
  const dayLabel = DAY_ORDER.find((d) => d.key === dayKey)?.label ?? "";
  let price = getPriceForDay(val, dayKey, dayLabel);
  if (price != null) return price;

  if (typeof val === "number" && !Number.isNaN(val)) return val;
  if (typeof val === "string" && val.trim() !== "") {
    const n = Number(val);
    if (!Number.isNaN(n)) return n;
  }
  if (val && typeof val === "object" && !Array.isArray(val)) {
    for (const { key, label } of DAY_ORDER) {
      const p = getPriceForDay(val, key, label);
      if (p != null) return p;
    }
    for (const key of ["price", "amount", "cost", "ticketPrice", "sellingPrice", "defaultPrice"]) {
      if (val[key] == null || val[key] === "") continue;
      const n = Number(val[key]);
      if (!Number.isNaN(n)) return n;
    }
  }
  return null;
}

function coerceTicketArray(raw) {
  if (Array.isArray(raw)) {
    return raw.filter((t) => t && typeof t === "object");
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return coerceTicketArray(parsed);
    } catch {
      /* ignore */
    }
  }
  return [];
}

export function pickTicketsFromEvent(event) {
  if (!event || typeof event !== "object") return [];
  const sources = [event];
  if (event.data && typeof event.data === "object" && !Array.isArray(event.data)) {
    sources.push(event.data);
  }
  const keys = ["tickets", "ticketList", "eventTickets", "Tickets", "ticket"];
  for (const src of sources) {
    for (const key of keys) {
      const list = coerceTicketArray(src[key]);
      if (list.length) return list;
    }
  }
  return [];
}

/** One table row per ticket tier (for day-wise price UI). */
export function buildTicketPriceRowsForEventDate(tickets, eventDateRaw) {
  if (!Array.isArray(tickets) || !tickets.length) return [];
  const rows = [];
  for (const ticket of tickets) {
    const label =
      ticket?.ticketType?.name ||
      ticket?.name ||
      ticket?.title ||
      "Ticket";
    const price = pickTicketDisplayPrice(ticket, eventDateRaw);
    if (price == null) continue;
    rows.push({ dayLabel: label, male: price, female: null });
  }
  return rows;
}

export function pickTicketDisplayPrice(ticket, eventDateRaw) {
  if (!ticket || typeof ticket !== "object") return null;
  const sources = [
    ticket.ticketPrice,
    ticket.price,
    ticket.discountedPrice,
    ticket.amount,
    ticket.cost,
    ticket.ticketType?.ticketPrice,
    ticket.ticketType?.price,
  ];
  for (const source of sources) {
    const resolved = getPriceForEventDate(source, eventDateRaw);
    if (resolved != null) return resolved;
  }
  return getPriceForEventDate(ticket, eventDateRaw);
}

export function buildDayPriceRows(malePrice, femalePrice) {
  const rows = [];
  for (const { key, label } of DAY_ORDER) {
    const m = getPriceForDay(malePrice, key, label);
    const f = getPriceForDay(femalePrice, key, label);
    if (m != null || f != null) {
      rows.push({ dayLabel: label, male: m, female: f });
    }
  }
  return rows;
}

/**
 * Prefer male/female fields; fall back to entryFee / ticketPrice when shaped the same.
 */
export function pickMaleFemalePrices(event) {
  if (!event || typeof event !== "object") {
    return { malePrice: null, femalePrice: null };
  }
  const malePrice =
    event.malePrice ??
    event.malePrices ??
    event.priceMale ??
    event.male_price ??
    null;
  const femalePrice =
    event.femalePrice ??
    event.femalePrices ??
    event.priceFemale ??
    event.female_price ??
    null;
  return { malePrice, femalePrice };
}

/**
 * If day-wise prices are stored as an array (e.g. [{ day, male, female }]).
 */
export function buildDayPriceRowsFromArray(arr) {
  if (!Array.isArray(arr) || !arr.length) return [];
  const labelFor = (raw) => {
    const s = String(raw || "").toLowerCase().trim();
    const found = DAY_ORDER.find(
      (d) => d.key === s || d.label.toLowerCase() === s
    );
    return found ? found.label : String(raw || "—");
  };
  const rows = [];
  for (const row of arr) {
    if (!row || typeof row !== "object") continue;
    const dayRaw = row.day ?? row.dayName ?? row.weekday ?? row.name;
    const m = row.male ?? row.malePrice ?? row.m;
    const f = row.female ?? row.femalePrice ?? row.f;
    const nm = m != null && m !== "" ? Number(m) : null;
    const nf = f != null && f !== "" ? Number(f) : null;
    if (nm == null && nf == null) continue;
    rows.push({
      dayLabel: labelFor(dayRaw),
      male: nm != null && !Number.isNaN(nm) ? nm : null,
      female: nf != null && !Number.isNaN(nf) ? nf : null,
    });
  }
  return rows;
}

export function buildDayPriceRowsForEvent(event, options = {}) {
  const { includeTicketTiers = true } = options;
  if (!event || typeof event !== "object") return [];
  const arr = event.dayWisePrices ?? event.day_wise_prices ?? event.pricesByDay;
  if (Array.isArray(arr) && arr.length) {
    const fromArr = buildDayPriceRowsFromArray(arr);
    if (fromArr.length) return fromArr;
  }
  const { malePrice, femalePrice } = pickMaleFemalePrices(event);
  let rows = buildDayPriceRows(malePrice, femalePrice);
  if (rows.length) return rows;
  const entry = event.entryFee ?? event.entry_fee;
  if (entry != null) {
    const entryObj = typeof entry === "object" && !Array.isArray(entry);
    if (!entryObj || Object.keys(entry).length > 0) {
      rows = buildDayPriceRows(entry, entry);
    }
  }
  if (rows.length) return rows;

  if (includeTicketTiers) {
    const tickets = pickTicketsFromEvent(event);
    const eventDate =
      event.startDate ??
      event.eventStartDate ??
      event.eventstartdate ??
      event.date ??
      event.eventDate;
    const ticketRows = buildTicketPriceRowsForEventDate(tickets, eventDate);
    if (ticketRows.length) return ticketRows;
  }

  return rows;
}

/** GeoJSON [lng, lat] or lat/lng fields */
export function pickCoordinates(ev) {
  if (!ev || typeof ev !== "object") return null;
  const coords = ev.coordinates?.coordinates;
  if (Array.isArray(coords) && coords.length >= 2) {
    const lng = Number(coords[0]);
    const lat = Number(coords[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }
  if (ev.latitude != null && ev.longitude != null) {
    const lat = Number(ev.latitude);
    const lng = Number(ev.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }
  return null;
}

export function googleMapsSearchUrl(lat, lng, address) {
  if (lat != null && lng != null) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  if (address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }
  return "";
}

/** Embed without API key (works for public display). */
export function googleMapsEmbedUrl(lat, lng) {
  if (lat == null || lng == null) return "";
  return `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
}

export function formatMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const num = Number(n);
  return `$${num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)}`;
}
